import { mediumImpact } from "@/utils/capacitorUtils";
import {
  IonButton,
  IonButtons,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { backspaceOutline } from "ionicons/icons";
import { Pencil } from "lucide-react";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const GolfHandicapInput: React.FC<{ initialValue: number, onChange: (value: number) => void, size?: 'sm' | 'md' | 'lg', fill?: 'outline' | 'solid' | 'clear' }> = ({ initialValue, size, onChange, fill }) => {
  const [handicap, setHandicap] = useState<string | null>(initialValue.toString() || null); // Stores the handicap value
  const [showNumpad, setShowNumpad] = useState(false); // Controls modal visibility
  const [isNewInput, setIsNewInput] = useState(true); // Track if this is a new input sequence
  const modal = useRef<HTMLIonModalElement>(null); // Ref for the Ionic modal
  const { t } = useTranslation();
  // Function to handle input from numpad
  const handleInput = (value: string) => {
    // Handle plus sign
    if (value === "+") {
      if (isNewInput || handicap === null || handicap === "") {
        setHandicap("+");
        setIsNewInput(false);
        return;
      }
      return; // Don't allow + in the middle of input
    }

    // Handle decimal point
    if (value === ".") {
      if (handicap?.includes(".")) return; // Don't allow multiple decimal points
      const newValue = (handicap || "0") + ".";
      setHandicap(newValue);
      setIsNewInput(false);
      return;
    }

    // Handle regular number input
    let newValue: string;
    if (isNewInput || handicap === null || handicap === "") {
      newValue = value;
      setIsNewInput(false);
    } else {
      newValue = handicap + value;
    }

    // Parse the numeric value for validation
    const numericValue = parseFloat(newValue.replace("+", ""));

    // Validate the handicap input rules
    if (
      newValue === "+" || // Allow "+" only at the start
      (newValue.startsWith("+") && /^(\+)?\d{0,1}(\.\d?)?$/.test(newValue) && numericValue <= 7.0) || // "+X" or "+X.X" format, max +7.0
      (!newValue.startsWith("+") && /^\d{0,2}(\.\d?)?$/.test(newValue)) // "XX" or "XX.X" format
    ) {
      setHandicap(newValue);
      mediumImpact();
    }
  };

  // Function to delete the last character
  const handleDelete = () => {
    if (handicap) {
      const newValue = handicap.slice(0, -1);
      setHandicap(newValue);
      if (newValue === "") {
        setIsNewInput(true);
      }
    }
    mediumImpact();

  };

  // Function to validate and close the modal
  const handleDone = () => {
    setIsNewInput(true); // Reset for next time modal is opened
    if (handicap === null) {
      setShowNumpad(false);
      return;
    }

    const numericValue = Number.parseInt(handicap.replace("+", ""), 10);

    if (handicap.startsWith("+") && numericValue <= 10) {
      // Valid positive handicap (e.g., "+10" or lower)
      setShowNumpad(false);
    } else if (!handicap || !handicap.startsWith("+") && numericValue >= 0 && numericValue <= 54) {
      // Valid non-positive handicap (e.g., "0" to "54")
      setShowNumpad(false);
    } else {
      alert("Invalid handicap. Please enter a value between +10 and 54.");
      setHandicap(""); // Reset the input on invalid entry
    }

    onChange(numericValue);
  };

  // Numpad buttons
  const numpadButtons = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0"];

  return (
    <>
      {/* Handicap Display Button */}
      <IonButton
        fill={fill || 'outline'}
        onClick={() => setShowNumpad(true)}
        type="button"
        className={"text-foreground rounded-md " + (size === 'lg' ? 'text-4xl' : '')}
      >
        <div className="flex font-semibold gap-4 items-center">
          {handicap ? 'HCP ' + handicap : t('no_handicap')}
          <Pencil />
        </div>
      </IonButton>

      {/* Handicap Input Modal */}
      <IonModal
        ref={modal}
        isOpen={showNumpad}
        onDidDismiss={() => setShowNumpad(false)}
        handleBehavior="none"
        className="auto-height"
      >
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonButton onClick={() => {
              setHandicap(null);
              handleDone();
            }}>{t('no_handicap')}</IonButton>
          </IonButtons>
          <IonTitle>Golf Handicap</IonTitle>
          <IonButtons slot="end">
            <IonButton className="font-bold" onClick={handleDone}>{t('done')}</IonButton>
          </IonButtons>
        </IonToolbar>

        <div className="p-4 inner-content">
          {/* Display the current handicap */}
          <div className="mb-4">
            <div className={"bg-input p-4 h-16 rounded-lg mb-2 text-center font-bold text-3xl"}>
              {handicap === null ? "No Index" : handicap}
            </div>
          </div>

          {/* Numpad Buttons */}
          <div className="grid grid-cols-3 gap-2 font-bold">
            {numpadButtons.map((btn) => (
              <button
                type="button"
                key={btn}
                className={`p-4 rounded-lg bg-secondary text-2xl`}
                onClick={() => handleInput(btn)}
              >
                {btn}
              </button>
            ))}
            <button
              type="button"
              onClick={handleDelete}
              className="p-4 rounded-lg bg-card hover:bg-secondary text-2xl "
            >
              <IonIcon icon={backspaceOutline} />
            </button>
          </div>
        </div>
      </IonModal>
    </>
  );
};

export default GolfHandicapInput;